import React, { useEffect, useState } from "react";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import {
  Button,
  Container,
  Card,
  FormControl,
  Grid,
  TextField,
  Box,
  Alert,
} from "@mui/material";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { currentBalanceApi } from "../../redux/actions/UsersAction";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Helper functions
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

const BalanceApi = () => {
  const [apiUrl, setApiUrl] = useState(
    localStorage.getItem("BalanceApiUrl") || ""
  );
  const [rawData, setRawData] = useState(
    localStorage.getItem("BalanceRawData") || ""
  );
  const [error, setError] = useState("");
  const [localApiResponse, setLocalApiResponse] = useState(
    JSON.parse(localStorage.getItem("BalanceLocalApiResponse")) || null
  );
  const [showRemoveButton, setShowRemoveButton] = useState(
    Boolean(localStorage.getItem("BalanceLocalApiResponse"))
  );
  const dispatch = useDispatch();

  const dataStructure = {
    userid: "string",
    request_amt: "string",
    apikey: "string",
  };

  const balanceApiResult = useSelector(
    (state) => state.users.balance_api_success_list
  );
  const updateResult = useSelector(
    (state) => state.users.update_balance_api_success
  );

  useEffect(() => {
    if (balanceApiResult && Object.keys(balanceApiResult).length > 0) {
      setLocalApiResponse(balanceApiResult);
      setShowRemoveButton(true);
    }
  }, [balanceApiResult]);

  useEffect(() => {
    if (updateResult) {
      const { status, message } = balanceApiResult || {};
      const toastMessage =
        message || "API call failed. Please check your input or try again.";

      if (status === 200) {
        toast.success(message || "API call was successful!");
      } else if (status === 401) {
        toast.error("Unauthorized access. Please check your credentials.");
      } else {
        toast.error(toastMessage);
      }
    }
  }, [updateResult]);

  useEffect(() => {
    if (localApiResponse) {
      localStorage.setItem(
        "BalanceLocalApiResponse",
        JSON.stringify(localApiResponse)
      );
      setShowRemoveButton(true);
    } else {
      localStorage.removeItem("BalanceLocalApiResponse");
      setShowRemoveButton(false);
    }
  }, [localApiResponse]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLocalApiResponse(null);
    setShowRemoveButton(false);

    if (!isValidUrl(apiUrl)) {
      setError("Invalid API URL format. Please enter a valid URL.");
      return;
    }

    if (!rawData.trim()) {
      setError("Raw Data cannot be blank.");
      return;
    }

    let params;
    try {
      params = JSON.parse(rawData);
    } catch (error) {
      setError("Invalid JSON format. Please check your raw data input.");
      return;
    }

    const invalidParams = Object.keys(dataStructure).filter(
      (key) => typeof params[key] !== dataStructure[key]
    );

    if (invalidParams.length > 0) {
      const errorMessage = `Invalid ${invalidParams.join(
        ", "
      )} parameter(s). Correct parameter(s) should be: ${Object.keys(
        dataStructure
      )
        .map((key) => `${key}: ${dataStructure[key]}`)
        .join(", ")}.`;
      setError(errorMessage);
      return;
    }

    try {
      await dispatch(currentBalanceApi(apiUrl, params));
      localStorage.setItem("BalanceApiUrl", apiUrl);
      localStorage.setItem("BalanceRawData", rawData);
    } catch (err) {
      setError("An error occurred while submitting the data.");
    }
  };

  const handleRemoveResponse = () => {
    setLocalApiResponse(null);
    setApiUrl("");
    setRawData("");
    setShowRemoveButton(false);
    localStorage.removeItem("BalanceApiUrl");
    localStorage.removeItem("BalanceRawData");
    toast.info("API response removed.");
  };

  return (
    <Page title="Balance Api">
      <HeaderBreadcrumbs
        heading="Balance Api & Join Game Api"
        links={[
          { name: "Dashboard", href: "/dashboard/app" },
          { name: "Balance Api & Join Game" },
        ]}
      />
      <Container maxWidth="lg">
        <Card>
          <Scrollbar>
            <Box
              component="form"
              autoComplete="off"
              onSubmit={handleSubmit}
              sx={{ padding: 2 }}
            >
              <Grid
                container
                spacing={2}
                sx={{ height: "400px", overflowY: "auto", mb: 2, mt: 1 }}
              >
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      type="text"
                      label="API URL"
                      variant="outlined"
                      value={apiUrl}
                      onChange={(e) => setApiUrl(e.target.value)}
                      required
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      minRows={5}
                      maxRows={10}
                      type="text"
                      label="Raw Data"
                      variant="outlined"
                      value={rawData}
                      onChange={(e) => setRawData(e.target.value)}
                      required
                    />
                  </FormControl>
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mt: 2,
                    gap: 2,
                  }}
                >
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    type="submit"
                  >
                    Save
                  </Button>
                  {showRemoveButton && (
                    <Button
                      size="large"
                      color="secondary"
                      variant="contained"
                      onClick={handleRemoveResponse}
                    >
                      Remove API Response
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Scrollbar>
        </Card>
        {localApiResponse && Object.keys(localApiResponse).length > 0 && (
          <Box mt={4}>
            <Card>
              <Box
                p={2}
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  overflowX: "auto",
                  maxHeight: "400px",
                }}
              >
                <h2>API Response</h2>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    backgroundColor: "#1e1e1e",
                    color: "#dcdcdc",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  {JSON.stringify(localApiResponse, null, 2)}
                </pre>
              </Box>
            </Card>
          </Box>
        )}
      </Container>
      <ToastContainer />
    </Page>
  );
};

export default BalanceApi;
